import React from 'react'
import * as Teeth from '../tooth-svgs'
import { IDentalData } from '@dataTypes/chartingFeature.types'
import { ChartingView } from './ChartingViewToggle.component'
import {
  getConditionProps,
  getDentalDataForTooth,
} from '@utils/dental-data.utils'
import {
  buildToothViewModel,
  ToothSvgRenderProps,
} from './tooth-view-model.utils'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import ToothNumberLabel from './ToothNumberLabel.component'
import ToothPerioLine from './ToothPerioLine.component'
import {
  getPerioSurfaceForPart,
  ToothPerioSites,
} from '@utils/perio-line.utils'
import './Tooth.css'
import './ToothTooltip.css'

type ToothPart =
  | 'number'
  | 'crownTop'
  | 'crownBottom'
  | 'base'
  | 'palatal'
  | 'lingual'

interface ToothCardProps {
  id: number
  selected: boolean
  onSelect: (id: number) => void
  parts: ToothPart[]
  dentalData: IDentalData[]
  view: ChartingView
  /** When true, shows treatment-plan reference styling (dashed border). */
  highlightTreatmentPlan?: boolean
  /** When true, disables click selection (read-only chart). */
  readOnly?: boolean
  /** Per-tooth PD sites when perio layer is on. */
  perioSites?: ToothPerioSites
}

type ToothSvgKey = Extract<
  keyof typeof Teeth,
  | `Tooth${number}`
  | `Tooth${number}CrownTop`
  | `Tooth${number}CrownBottom`
  | `ToothPalatal${number}`
  | `ToothLingual${number}`
>

const TOOTH_COMPONENTS: Partial<
  Record<ToothSvgKey, React.ComponentType<ToothSvgRenderProps>>
> = Teeth
const TOOTH_TOOLTIP_ID = 'charting-tooth-tooltip'

const getToothComponentKey = (
  part: Exclude<ToothPart, 'number'>,
  id: number,
): ToothSvgKey => {
  if (part === 'base') return `Tooth${id}` as ToothSvgKey
  if (part === 'crownTop') return `Tooth${id}CrownTop` as ToothSvgKey
  if (part === 'crownBottom') return `Tooth${id}CrownBottom` as ToothSvgKey
  if (part === 'palatal') return `ToothPalatal${id}` as ToothSvgKey
  return `ToothLingual${id}` as ToothSvgKey
}

const Tooth = ({
  id,
  selected,
  onSelect,
  parts,
  dentalData,
  view,
  highlightTreatmentPlan = false,
  readOnly = false,
  perioSites,
}: ToothCardProps): React.ReactElement => {
  const { showMissingTeeth, showPrimaryTeeth, chartingSystem } =
    useClinicChartingPreferences()
  const conditionProps = getConditionProps(dentalData, id)
  const hasBleeding = getDentalDataForTooth(dentalData, id).some(
    (condition) => condition.condition === 'bleeding',
  )
  const { toothComponentProps, tooltipRows } = buildToothViewModel({
    toothId: id,
    conditionProps,
    hasBleeding,
    showMissingTeeth,
    showPrimaryTeeth,
  })

  const renderPerioOverlay = (
    part: 'base' | 'palatal' | 'lingual',
  ): React.ReactNode => {
    if (!perioSites) {
      return null
    }
    const surface = getPerioSurfaceForPart(part, id, view)
    const sitesA =
      surface === 'facial' ? perioSites.facialA : perioSites.lingualA
    const sitesB =
      surface === 'facial' ? perioSites.facialB : perioSites.lingualB
    return (
      <ToothPerioLine
        toothId={id}
        sitesA={sitesA}
        sitesB={sitesB}
        isUpper={id >= 1 && id <= 16}
      />
    )
  }

  const wrapWithSvg = (
    Comp: React.ComponentType<ToothSvgRenderProps>,
    perioPart?: 'base' | 'palatal' | 'lingual',
  ): React.ReactElement => (
    <div
      className="tooth-part-svg-wrap"
      {...(perioPart && perioSites
        ? { 'data-perio-anchor': `${id}-${perioPart}` }
        : {})}
    >
      <Comp {...toothComponentProps} />
      {perioPart ? renderPerioOverlay(perioPart) : null}
    </div>
  )

  const renderPart = (part: ToothPart): React.ReactNode => {
    // Don't render base part if tooth is from wrong jaw for the current view
    if (part === 'base') {
      if (view === 'upper' && id >= 17 && id <= 32) {
        return null
      }
      if (view === 'lower' && id >= 1 && id <= 16) {
        return null
      }
    }

    if (view === 'full') {
      if (part === 'palatal' || part === 'lingual') {
        return null
      }
    }
    if (view === 'upper') {
      if (part === 'lingual' || part === 'crownBottom') {
        return null
      }
    }
    if (view === 'lower') {
      if (part === 'palatal' || part === 'crownTop') {
        return null
      }
    }
    if (part === 'number') {
      return (
        <div className="tooth-part tooth-part-number">
          <ToothNumberLabel
            universalToothNumber={id}
            numberingSystem={chartingSystem.tooth_numbering_system}
          />
        </div>
      )
    }

    const key = getToothComponentKey(part, id)
    const Comp = TOOTH_COMPONENTS[key]

    if (part === 'palatal' || part === 'lingual') {
      if (part === 'palatal') {
        if (Comp) {
          return (
            <div className="tooth-part tooth-part-palatal">
              {wrapWithSvg(Comp, 'palatal')}
            </div>
          )
        }
      }

      if (part === 'lingual') {
        if (Comp) {
          return (
            <div className="tooth-part tooth-part-lingual">
              {wrapWithSvg(Comp, 'lingual')}
            </div>
          )
        }
      }

      return <div className="tooth-part" />
    }

    if (!Comp) {
      return (
        <div className="tooth-part tooth-part-fallback">
          {part}
          <br />
          {String(key)}
        </div>
      )
    }

    const isCrownTop = part === 'crownTop'
    const isLowerBase = part === 'base' && id >= 17 && id <= 32
    const partClassNames = ['tooth-part']

    if (isCrownTop) {
      partClassNames.push('tooth-part-center')
    }

    if (isLowerBase) {
      partClassNames.push('tooth-part-base-lower')
    }

    if (part === 'base') {
      partClassNames.push('tooth-part-base')
    }

    const perioPart = part === 'base' ? 'base' : undefined

    return (
      <div className={partClassNames.join(' ')}>
        {wrapWithSvg(Comp, perioPart)}
      </div>
    )
  }

  const tooltipHtml =
    tooltipRows.length === 0
      ? `<div class="tooth-tooltip-content"><div class="tooth-tooltip-header">Tooth ${id}</div><div class="tooth-tooltip-empty">No conditions recorded</div></div>`
      : [
          `<div class="tooth-tooltip-content">`,
          `<div class="tooth-tooltip-header">Tooth ${id}</div>`,
          `<div class="tooth-tooltip-conditions">`,
          tooltipRows
            .map(
              (row) =>
                `<div class="tooth-tooltip-condition">` +
                `<div class="tooth-tooltip-condition-line">` +
                `<span class="tooth-tooltip-label">${row.label}:</span>` +
                `<span class="tooth-tooltip-details"> ${row.details || 'Active'}</span>` +
                `</div>` +
                `</div>`,
            )
            .join(''),
          `</div>`,
          `</div>`,
        ].join('')
  // Keep side placement behavior from the original tooltip.
  const tooltipPlace =
    (id >= 1 && id <= 8) || (id >= 25 && id <= 32) ? 'right' : 'left'

  return (
    <>
      <div
        className={`tooth-card${selected ? ' tooth-card--selected' : ''}${highlightTreatmentPlan ? ' tooth-card--treatment-plan' : ''}${readOnly ? ' tooth-card--read-only' : ''}`}
        onClick={
          readOnly
            ? undefined
            : () => {
                onSelect(id)
              }
        }
        data-tooltip-id={TOOTH_TOOLTIP_ID}
        data-tooltip-html={tooltipHtml}
        data-tooltip-place={tooltipPlace}
      >
        {parts.map((part) => (
          <React.Fragment key={`${id}-${part}`}>
            {renderPart(part)}
          </React.Fragment>
        ))}
      </div>
    </>
  )
}

const areToothPropsEqual = (
  prev: ToothCardProps,
  next: ToothCardProps,
): boolean => {
  if (prev.id !== next.id) return false
  if (prev.selected !== next.selected) return false
  if (prev.highlightTreatmentPlan !== next.highlightTreatmentPlan) return false
  if (prev.readOnly !== next.readOnly) return false
  if (prev.view !== next.view) return false
  if (prev.dentalData !== next.dentalData) return false
  if (prev.perioSites !== next.perioSites) return false
  if (prev.parts.length !== next.parts.length) return false

  for (let i = 0; i < prev.parts.length; i += 1) {
    if (prev.parts[i] !== next.parts[i]) {
      return false
    }
  }

  return true
}

export default React.memo(Tooth, areToothPropsEqual)
